import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { useValue } from 'firebase-utils'
import Spinner from 'spinner'
import NavigationProvider from './NavigationProvider'
import { BrowserRouter } from 'react-router-dom'

// Possible user interfaces:
import NavUIV1 from './NavUIV1/NavUIV1'
import NavUIV2 from './NavUIV2/NavUIV2'
import { ScreenSizeContext } from 'providers'
import ContentArea from './CommonUI/ContentArea'
/**
 *
 */
export { NavigationContext } from './NavigationContext'
/**
 *
 */
export { default as SidebarV2Link } from './NavUIV2/SidebarV2/SidebarV2Link'
/**
 *
 */
export { default as NavItemIcon } from './CommonUI/NavItemIcon'

/**
 * The navigation configuration object typically sourced from remote config.
 */
export interface NavConfig {
    ui: string;
    navItems: NavItem[];

    // Upon entering the app, on "/" or the set basePath, the user will be
    // navigated to this nav item. Defaults to the first nav item in navItems[]
    initialNavItemID?: string;

    // if set the user is redirected to this nav item on invalid paths,
    // overrides the default behavior of showing the 404 / Page not found page.
    invalidPathNavItemID?: string;
}

/**
 * For external links:
 */
export interface NavItemLinkProps {
    href: string; // The URL to be opened / navigated to
    target?: string; // The target attribute
    sendToken?: boolean;
}

/**
 *
 */
export interface NavItem {
    id: string; // A unique identifier, conventionally use lower case with dashes (eg. "personal-settings").
    title: string; // A title to be displayed to the user representing this nav item.
    route?: {
        path: string; // Used by internal links (eg. "admin"). This is the path relative to its parent.
    };
    link?: NavItemLinkProps // Alternatively the nav item can be just a link to some place outside of the app.
    componentProps?; // additional data that will be sent to the component on render.
    componentName?: string; // Only required for nav items without children (leaf nodes in the nav tree).
    badgeComponentName?: string; // The name of the component (within the componentMap) that should be used to render a badge (eg. displaying a count of unread messages)
    enabled?: boolean; // If false, the nav item will be ignored from UI and the routing system.
    userRoles?: string[]; // eg. ["account", "extension"], when omitted shows the nav item to all user roles.
    userTypes?: string[]; // eg. ["nxt", "classic"], when omitted shows the nav item to all user types.
    iconHTML?: string; // HTML as a string, used to display an icon for this nav item.
    pinned?: boolean; // If true, the nav item will be included in a list of pinned items (for example at the bottom of a sidebar). This can be useful to distinguish key nav items.
    children?: NavItem[]; // An array of child nav items, if this nav item is to function as a parent nav item.
    buttonComponentName?: string; // The component that will be used to render the nav item as a Button.
}

/**
 * Object mapping componentNames to the corresponding React component class/function
 */
export interface NavComponentMap {
    [componentName: string]: React.ComponentType;
}

const NavigationUIs = {
    NavUIV1,
    NavUIV2
}

interface NavigationProps {
    basePath: string;
    remoteConfigKey: string;
    componentMap: NavComponentMap;
    children?: React.ReactChildren;
    showContentOnly?: boolean;
}

/**
 * A Navigation system that reads navigation config from remote config and renders a designed navigation UI,
 * to allow the user to navigate and browse through a hierachy of navigation items/pages.
 * @param {string} basePath - A path to be used to prefix all routes (eg. "/" or "/app").
 * @param {string} remoteConfigKey - The key identifying the firebase remote config JSON used to configure this navigation component.
 * @param {object} componentMap - An object that serves as a dictionary, mapping component names (strings) to the actual component function/class.
 * @param {React.ReactChildren} [children] - An children, these will be displayed above the top of the navigation UI. Useful for status banners, notfications, etc.
 * @param {boolean} [showContentOnly] - If true, only the content area of the currently active nav item will be shown (hiding all navigation UI such as sidebars, topbars, etc.)
 */
const Navigation = ({ basePath, remoteConfigKey, componentMap, children, showContentOnly }: NavigationProps) => {
    const navConfigValue: string = useValue(remoteConfigKey, { defaultRequired: false })

    if (!navConfigValue) {
        return (
            <Spinner size="sm" />
        )
    }

    const navConfig: NavConfig = JSON.parse(navConfigValue)

    if (!navConfig.ui) {
        // A fatal error, with no viable fallback, so throw an error (would cause an error page)
        throw new Error('NavConfig: no UI selected, see navConfig.ui')
    }

    if (!(navConfig.ui in NavigationUIs)) {
        // This is also a fatal error, with no viable fallback, so throw an error (would cause an error page)
        throw new Error(`NavConfig invalid value for ui field: ${navConfig.ui}, valid options: ${Object.keys(NavigationUIs)}`)
    }

    let NavigationUI = NavigationUIs[navConfig.ui]

    // Temporary code: (delete once NavUIV2 fully supports all sizes of screens)
    const screenSize = useContext(ScreenSizeContext)
    const isSmallView = screenSize.mobile || screenSize.tablet
    if (NavigationUI === NavUIV2 && isSmallView) {
        NavigationUI = NavUIV1
    }
    // ^ End of temporary code

    return (
        <BrowserRouter>
            <NavigationProvider
                basePath={basePath}
                navConfig={navConfig}
                componentMap={componentMap}
            >
                {showContentOnly
                    ? (
                        <ContentArea />
                    )
                    : (
                        <>
                            {children}
                            <NavigationUI />
                        </>
                    )}
            </NavigationProvider>
        </BrowserRouter>
    )
}

// Note: Comments here get populated into the react styleguide based docs:
Navigation.propTypes = {
    /**
     * A path to be used to prefix all routes (eg. "/" or "/app").
     */
    basePath: PropTypes.string,

    /**
     * The key identifying the firebase remote config JSON used to configure this navigation component.
     */
    remoteConfigKey: PropTypes.string,

    /**
     * An object that serves as a dictionary, mapping component names (strings) to the actual component function/class.
     */
    componentMap: PropTypes.object,

    /**
     * If true, only the content area of the currently active nav item will be shown (hiding all navigation UI such as sidebars, topbars, etc.)
     */
    showContentOnly: PropTypes.bool
}

export default Navigation
